// =============================================
// VALVES & COMPONENT ISOLATION DATA
// Merged detailed aviation specifications
// =============================================

export const VALVES_DATA = [
    {
        id: 'throttle-valve',
        name: 'The Throttle Valve (Butterfly Valve)',
        icon: '🦋',
        location: 'In the carburetor barrel, between the venturi and the engine intake manifold.',
        function: 'Controls the total mass volume of the fuel/air mixture entering the cylinders, thereby dictating engine RPM and power output.',
        examNote: 'It does not meter fuel directly; it regulates the mixture volume.',
        trigger: 'Pilot throttle lever movement via mechanical linkage.',
        movement: 'Rotates from nearly closed (idle stop) to parallel with the barrel (full throttle).',
        pressureFlow: 'Restricts the intake path to create a pressure drop downstream. At idle, it is nearly closed, creating high manifold vacuum.',
        stuckOpen: 'Engine runs at high power/RPM regardless of throttle position. Extremely dangerous on start or landing.',
        stuckClosed: 'Engine remains at idle or shut down; cannot develop power for takeoff.',
        system: 'control',
        camera: { pos: { x: 5, y: 3, z: 8 }, target: { x: 0, y: -2, z: 0 } }
    },
    {
        id: 'needle-valve',
        name: 'The Float Needle Valve & Seat',
        icon: '🔽',
        location: 'At the fuel inlet port, inside the float chamber.',
        function: 'Actuated by the buoyancy of the float. As fuel level drops, the needle lowers off its seat to let pump/gravity fuel in. As the bowl fills, it pushes the needle up into the seat to shut off the flow, maintaining a constant unmetered fuel level.',
        examNote: 'Essential for maintaining the static fuel level required for accurate metering.',
        trigger: 'Float buoyancy changes based on fuel level in the chamber.',
        movement: 'Vertical travel in and out of a precision-machined conical seat.',
        pressureFlow: 'Regulates supply pressure to atmospheric bowl pressure. Maintains fuel level slightly below the discharge nozzle tip.',
        stuckOpen: 'Bowl overflows, causing fuel dripping from throat and a dangerously rich mixture.',
        stuckClosed: 'Fuel starvation; float bowl empties and engine stops.',
        system: 'fuel',
        camera: { pos: { x: -6, y: 4, z: 6 }, target: { x: -2, y: 1, z: 1 } }
    },
    {
        id: 'mixture-control',
        name: 'The Mixture Control Valve (Rotary or Plunger Type)',
        icon: '🎛️',
        location: 'Between the float chamber and the main metering jet.',
        function: 'Adjusts the size of the fuel passage to restrict flow, allowing the pilot to lean the mixture as air density decreases at altitude. Also completely shuts off fuel flow in the "Idle Cut-Off" position.',
        examNote: 'In standard aircraft carburetors, this is the only intentional manual mixture adjustment available in flight.',
        trigger: 'Pilot mixture lever/cable movement.',
        movement: 'Rotary plate or sliding plunger that varies the orifice size.',
        pressureFlow: 'Limits the amount of fuel that can reach the main metering jet from the bowl.',
        stuckOpen: 'Inability to lean at altitude; excessive fuel consumption and plug fouling.',
        stuckClosed: 'Engine will not start or stops immediately (Idle Cut-Off position).',
        system: 'mixture',
        camera: { pos: { x: 0, y: 0, z: 5 }, target: { x: 0, y: 0, z: 0 } }
    },
    {
        id: 'economizer',
        name: 'The Economizer Valve (Power Enrichment)',
        icon: '⚡',
        location: 'Parallel to the main metering system, mechanically linked to the throttle linkage.',
        function: 'Remains closed at cruise settings. Opens automatically at high power settings (usually above 60-70% throttle) to supply additional, unmetered fuel. This artificially rich mixture cools the cylinders to prevent detonation during high-stress operations like takeoff.',
        examNote: 'A failure in this valve can cause severe engine overheating at full throttle.',
        trigger: 'Mechanical linkage or manifold pressure sensor at high power settings.',
        movement: 'Spring-loaded valve that lifts at a specific throttle threshold.',
        pressureFlow: 'Opens a secondary parallel path for fuel directly to the main discharge nozzle.',
        stuckOpen: 'Excessive fuel consumption at cruise; rough running at low power.',
        stuckClosed: 'Dangerously lean mixture at full power; risk of detonation and engine damage.',
        system: 'mixture',
        camera: { pos: { x: 4, y: 2, z: -4 }, target: { x: 1, y: 0, z: -1 } }
    },
    {
        id: 'poppet-valve',
        name: 'The Poppet Valve (Pressure-Injection Models)',
        icon: '🎯',
        location: 'Central shaft of the fuel regulator unit (e.g., Bendix RSA).',
        function: 'Opens and closes to meter fuel based on the pressure differential (Air Metering Force) between impact air and venturi suction, opposing the unmetered fuel pressure.',
        examNote: 'Main metering component for pressure-injection systems; more precise than float types.',
        trigger: 'Differential air pressure across diaphragms.',
        movement: 'Linear movement against a spring to modulate fuel orifice.',
        system: 'fuel',
        camera: { pos: { x: 0, y: 8, z: 0 }, target: { x: 0, y: 0, z: 0 } }
    },
    {
        id: 'idle-mixture-screw',
        name: 'Idle Mixture Adjustment Screw',
        icon: '🔩',
        location: 'Downstream of the throttle valve.',
        function: 'Fine-tunes the fuel-air ratio at idle RPMs by restricting the idle fuel passage.',
        examNote: 'Adjusting this is part of standard ground-run maintenance.',
        trigger: 'Manual technician adjustment via screwdriver.',
        movement: 'Tapered needle threading into the idle discharge passage.',
        stuckOpen: 'Rough, over-rich idle; sooty spark plugs.',
        stuckClosed: 'Engine stalls as soon as throttle is closed to the idle stop.',
        system: 'mixture',
        camera: { pos: { x: 0, y: 0, z: 5 }, target: { x: 0, y: 0, z: 0 } }
    },
    {
        id: 'accel-pump-valves',
        name: 'Pump Inlet & Outlet Check Valves',
        icon: '↕️',
        location: 'Within the accelerator pump casting.',
        function: 'One-way valves ensuring fuel moves from the bowl into the pump (inlet) and then into the venturi (outlet) during acceleration shots.',
        examNote: 'Failure causes "flat spots" or hesitation during rapid throttle advancement.',
        system: 'fuel',
        camera: { pos: { x: 1.8, y: -0.5, z: -0.9 }, target: { x: 1.1, y: -0.5, z: -0.6 } }
    },
    {
        id: 'carb-heat-flap',
        name: 'Carburetor Heat Flap / Door',
        icon: '🔥',
        location: 'In the air intake box.',
        function: 'Redirects intake air through a shroud around the exhaust to prevent or clear carburetor icing.',
        examNote: 'Using carb heat results in a slight power loss due to decreased air density.',
        system: 'control',
        camera: { pos: { x: 1.2, y: 3.8, z: 0.5 }, target: { x: 0, y: 3.5, z: 0 } }
    }
];

export function renderValveCards(container) {
    container.innerHTML = `
        <div id="valves-side-panel" class="glass-panel">
            <h3>Internal Valve Directory</h3>
            <p class="subtitle">Select a component for 3D isolation and AME specs</p>
            <div id="valves-interaction-list">
                ${VALVES_DATA.map(v => `
                    <div class="valve-list-item" data-valve-id="${v.id}">
                        <div class="valve-item-title">${v.icon} ${v.name}</div>
                        <div class="valve-item-location">📍 ${v.location}</div>
                    </div>
                `).join('')}
            </div>
        </div>
        <div id="valve-detail-display" class="glass-panel">
             <div class="diagnosis-placeholder">
                <p>Select a valve from the directory to isolate it in 3D and view technical details.</p>
             </div>
        </div>
    `;
}

export function renderValveDetail(container, valveId) {
    const valve = VALVES_DATA.find(v => v.id === valveId);
    if (!valve) return;

    container.innerHTML = `
        <div class="valve-detail-header">
            <h4>${valve.icon} ${valve.name}</h4>
            <span class="system-badge system-${valve.system}">${valve.system.toUpperCase()}</span>
        </div>
        
        <div class="diag-grid">
            <div class="info-section">
                <label>Primary Function</label>
                <p>${valve.function}</p>
            </div>
            
            <div class="info-section">
                <label>Operation & Logic</label>
                <p><strong>Trigger:</strong> ${valve.trigger || 'Varies by model'}</p>
                <p><strong>Movement:</strong> ${valve.movement || 'Internal/Mechanical'}</p>
            </div>
        </div>

        <div class="info-section alert-box ${valve.stuckOpen ? 'visible' : 'hidden'}">
            <label>⚠️ Failure Mode: Stuck Open / Leaking</label>
            <p>${valve.stuckOpen || 'Increased fuel consumption / abnormal richness.'}</p>
        </div>

        <div class="info-section alert-box warn ${valve.stuckClosed ? 'visible' : 'hidden'}">
            <label>🚨 Failure Mode: Stuck Closed / Blocked</label>
            <p>${valve.stuckClosed || 'Fuel starvation or inability to reach high power.'}</p>
        </div>

        <div class="exam-note-box">
            <label>🎓 FAA/AME Exam Note</label>
            <p>${valve.examNote}</p>
        </div>
    `;
}
